import {
  isDashboardRoute,
  isUserModuleRoute,
  parsePatientRoute,
  paths,
  patientModulePath,
  type PatientModuleId,
} from './services/navigation.service.ts';
import { getCurrentPracticeId } from './services/practices.service.ts';
import type { Patient } from './services/patients.service.ts';

type NavigateFn = (to: string) => void;
type SelectPatientFn = (patient: Patient) => void;
type ClearPatientFn = () => void;
type GetCurrentPatientFn = () => Patient | null;
type OpenChatFn = (conversationId: number) => void;

const bridge: {
  navigate?: NavigateFn;
  selectPatient?: SelectPatientFn;
  clearPatient?: ClearPatientFn;
  getCurrentPatient?: GetCurrentPatientFn;
  openChat?: OpenChatFn | null;
} = {};

export function registerAppBridge(handlers: Partial<typeof bridge>): void {
  Object.assign(bridge, handlers);
}

export function registerChatDockHandler(fn: OpenChatFn | null): void {
  bridge.openChat = fn;
}

export function appNavigate(to: string): void {
  bridge.navigate?.(to);
}

export function appSelectPatient(patient: Patient): void {
  bridge.selectPatient?.(patient);
}

export function appClearPatient(): void {
  bridge.clearPatient?.();
}

export function appGetCurrentPatient(): Patient | null {
  return bridge.getCurrentPatient?.() ?? null;
}

/** Ouvre une conversation dans le dock flottant (fallback : navigation vers la page messages). */
export function appOpenMessageConversation(conversationId: number): void {
  if (bridge.openChat) {
    bridge.openChat(conversationId);
    return;
  }
  appNavigate(paths.messages(getCurrentPracticeId(), conversationId));
}

/** Sélectionne le patient et l'applique au contexte courant.
 *  - Dashboard ou module personnalisé (`/m/{id}`) → on y reste.
 *  - Module patient (`/patients/{uuid}/{moduleId}`) → on bascule sur le même
 *    module pour le nouveau patient.
 *  - Sinon → retour au tableau de bord. */
export function appFocusPatientOnDashboard(patient: Patient): void {
  appSelectPatient(patient);
  if (typeof window === 'undefined') return;
  const path = window.location.pathname;
  if (isDashboardRoute(path) || isUserModuleRoute(path)) return;
  const patientRoute = parsePatientRoute(path);
  if (patientRoute) {
    appNavigate(
      patientModulePath(
        getCurrentPracticeId(),
        patient.uuid,
        patientRoute.moduleId,
      ),
    );
    return;
  }
  appNavigate(paths.dashboard(getCurrentPracticeId()));
}

/** Sélectionne le patient et ouvre son dossier (module par défaut : clinique). */
export function appOpenPatient(
  patient: Patient,
  moduleId: PatientModuleId = 'clinical',
): void {
  appSelectPatient(patient);
  appNavigate(
    patientModulePath(getCurrentPracticeId(), patient.uuid, moduleId),
  );
}
